import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatPrice } from '@/utils/prices';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '@/contexts/cartContext';
import { useAuthContext } from '@/contexts/authContext';
import PaymentNotice from '@/components/pages/subscription/paymentDialog/notice';
import SimpleLoader from '@/components/loaders/simpleLoader';
import { FormError } from '@/components/commons/formError';
import { useNavigate } from 'react-router';
import { captureException } from '@sentry/react';

interface PaymentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PaymentDialogContent({ isOpen, onOpenChange }: PaymentDialogProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { account } = useAuthContext();
  const navigate = useNavigate();
  const { totalIncludingVat, currency, prices, totalWithoutVat } = useCartContext();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { handleSubmit } = useForm();

  const onError = () => {
    setErrorMessage(t('ErrorMessage_ErrorMessage'));
  };

  const onSubmit = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage(undefined);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements: elements,
        confirmParams: {
          return_url: `${window.location.origin}/status`,
        },
        redirect: 'if_required',
      });

      if (error) {
        captureException(error);
        setErrorMessage(error.message);
      } else if (account?._id && paymentIntent?.id) {
        console.log('Payment succeeded', paymentIntent);
        onOpenChange(false);
        navigate(`/status?payment_intent=${paymentIntent.id}`);
      } else {
        console.error('Payment intent failed', paymentIntent);
        setErrorMessage(t('ErrorMessage_ErrorMessage'));
      }
    } catch (error) {
      console.error('Payment error', error);
      setErrorMessage(t('ErrorMessage_ErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        onInteractOutside={e => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('Payment_Information')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder:
                currency === 'eur'
                  ? ['card', 'sepa_debit', 'apple_pay', 'google_pay']
                  : ['card', 'apple_pay', 'google_pay'],
              wallets: {
                applePay: 'auto',
                googlePay: 'auto',
              },
              fields: {
                billingDetails: {
                  address: 'if_required',
                },
              },
            }}
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading
              ? t('Payment_Processing_Label')
              : t('payment_amount', { amount: formatPrice(totalIncludingVat, currency) })}
          </Button>
          {errorMessage && <FormError error={errorMessage} />}
          <PaymentNotice prices={prices} totalWithoutVat={totalWithoutVat} />
        </form>
      </DialogContent>

      <SimpleLoader
        isLoading={loading}
        title={t('payment_in_progress_title')}
        description={t('payment_in_progress_description')}
        mode="modal"
      />
    </Dialog>
  );
}
